import React from "react";
import { Link } from "react-router-dom";
import {
  DollarSign,
  Gift,
  Shirt,
  Ticket,
  Tag,
  MapPin,
  Crown,
  Target,
  Compass,
  Landmark,
  Sparkles,
  Thermometer,
  Trophy,
  Flame,
  Users,
  Share2,
  Bell,
  ArrowRight,
} from "lucide-react";

const GREEN = "#76B82A";
const GOLD = "#D7A62A";
const RED = "#E53935";
const CREAM = "#F5F2E8";

// -------------------- Prize Types --------------------
const PRIZES = [
  { Icon: DollarSign, label: "Real cash",        note: "Envelopes stuffed with bills." },
  { Icon: Gift,       label: "Gift cards",       note: "Local coffee, BBQ, gear shops." },
  { Icon: Shirt,      label: "Merch",            note: "Shirts, hats, decals, stickers." },
  { Icon: Ticket,     label: "Experiences",      note: "Tickets, passes, VIP access." },
  { Icon: Tag,        label: "Sponsor perks",    note: "In‑app coupons at branded pins." },
];

// -------------------- Hunt Modes --------------------
const MODES = [
  {
    Icon: MapPin,
    tag: "CLASSIC",
    tagColor: GREEN,
    title: "Cash Drops",
    body: "Our flagship hunt. We hide a real envelope, post clues one at a time, and the first hunter to the code keeps the prize.",
    testid: "mode-classic",
  },
  {
    Icon: Crown,
    tag: "GOLDEN",
    tagColor: GOLD,
    title: "Golden Drops",
    body: "Sponsor‑funded mega drops. Bigger payout, tighter GPS hints, gold‑bordered winner card — and a permanent Golden badge.",
    testid: "mode-golden",
  },
  {
    Icon: Target,
    tag: "BOUNTY",
    tagColor: GOLD,
    title: "Bounty Drops",
    body: "Walk into the sponsor's business, scan the QR, grab the prize. Guaranteed foot traffic for the shop, guaranteed loot for you.",
    testid: "mode-bounty",
  },
  {
    Icon: Compass,
    tag: "LOOT QUEST",
    tagColor: RED,
    title: "Physical Chests",
    body: "Hidden lockboxes with unique claim codes. Find the chest, punch the code online, reveal your prize (cash, card, merch, whatever's inside).",
    testid: "mode-loot-quest",
  },
  {
    Icon: Landmark,
    tag: "WAYMARKS",
    tagColor: GREEN,
    title: "Permanent Pins",
    body: "Historic plaques, murals, quirky spots. Walk up, unlock an AI‑written fun fact, collect a Passport stamp — and sometimes a coupon from a local sponsor.",
    testid: "mode-waymarks",
  },
];

// -------------------- Game Mechanics --------------------
const MECHANICS = [
  {
    Icon: Thermometer,
    title: "Warmer / Colder hints",
    body: "Opt‑in GPS thermometer with trend arrows (↑ getting warmer, ↓ cooling off). Never exact — just close enough to keep the chase honest.",
  },
  {
    Icon: Sparkles,
    title: "Meet the Founder",
    body: "Our founder roams live drops. Get within 50 ft and claim a one‑of‑a‑kind badge. Sometimes he goes stealth — crack the riddle and you'll still earn it.",
  },
  {
    Icon: Trophy,
    title: "Leaderboards",
    body: "Top Finders, Fastest Finds, Waymark Passport, and weekly city boards. Earn badges. Flex your streak.",
  },
  {
    Icon: Flame,
    title: "Streaks & Freezes",
    body: "Check in daily to build a streak. Life got in the way? Use one Streak Freeze a month and keep your run alive.",
  },
  {
    Icon: Users,
    title: "Refer a Hunter",
    body: "Every account has a referral link. When your friend joins, you both get a streak bonus and a Recruiter badge.",
  },
  {
    Icon: Share2,
    title: "Winners Wall",
    body: "Every find gets a sharable card — selfie, prize type, city, story. The wall is the proof. The wall is the hype machine.",
  },
];

// -------------------- Classic 5 Steps (updated) --------------------
const STEPS = [
  { n: "01", t: "We hide the loot.",   d: "Cash, gift cards, merch, or experiences — stashed in safe, public spots across your city." },
  { n: "02", t: "We post the clues.",  d: "One clue at a time, on a timer, right in the app. Notifications fire the second a drop goes live." },
  { n: "03", t: "You hunt it down.",   d: "Follow the clues. Optional warmer/colder GPS hints keep it fair without giving it away." },
  { n: "04", t: "You prove the find.", d: "Punch the unique code, snap a selfie, upload your proof shot. We verify — no hacks, no favorites." },
  { n: "05", t: "You keep it.",        d: "No fees, no catch, no lottery. Whatever's in the envelope is yours." },
];

export default function HowItWorksPage() {
  return (
    <div className="py-8 space-y-14" data-testid="how-it-works-page">
      {/* HERO */}
      <div>
        <div className="overline">Clues. Real rewards. Real community.</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
          How it <span style={{ color: GREEN }}>works</span>
        </h1>
        <p className="mt-4 text-[color:rgba(245,242,232,0.75)] max-w-2xl">
          The Local Loot isn't just cash. It's a city‑wide game with five hunt modes, permanent landmarks,
          and local businesses backing real rewards you actually want.
        </p>
      </div>

      {/* WHAT YOU CAN WIN */}
      <section data-testid="prize-types">
        <div className="overline mb-3" style={{ color: GOLD, borderColor: GOLD }}>What you can win</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-5">
          It's not just <span style={{ color: GOLD }}>cash.</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {PRIZES.map(({ Icon, label, note }) => (
            <div
              key={label}
              className="loot-card-flat p-4 flex flex-col gap-2"
              data-testid={`prize-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "rgba(215,166,42,0.12)", color: GOLD }}
              >
                <Icon size={20} />
              </div>
              <div className="font-display uppercase text-base leading-tight">{label}</div>
              <div className="text-xs text-[color:rgba(245,242,232,0.6)] leading-snug">{note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* THE 5 STEPS */}
      <section>
        <div className="overline mb-3">The playbook</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-5">
          Five steps. <span style={{ color: GREEN }}>Zero gimmicks.</span>
        </h2>
        <div className="space-y-4">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              className="loot-card p-6 md:p-8 flex flex-col md:flex-row gap-4 md:gap-8"
              data-testid={`how-step-${i}`}
            >
              <div className="font-display text-6xl md:text-7xl leading-none shrink-0" style={{ color: GOLD }}>
                {s.n}
              </div>
              <div>
                <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight">{s.t}</h3>
                <p className="mt-2 text-[color:rgba(245,242,232,0.7)] max-w-xl">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HUNT MODES */}
      <section data-testid="hunt-modes">
        <div className="overline mb-3" style={{ color: RED, borderColor: RED }}>Five ways to play</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-5">
          Hunt <span style={{ color: RED }}>modes</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MODES.map(({ Icon, tag, tagColor, title, body, testid }) => (
            <div
              key={tag}
              className="loot-card p-6 flex gap-4"
              data-testid={testid}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${tagColor}22`, color: tagColor }}
              >
                <Icon size={22} />
              </div>
              <div className="min-w-0">
                <span
                  className="tag-pill"
                  style={{ color: tagColor, borderColor: tagColor }}
                >
                  {tag}
                </span>
                <h3 className="font-display uppercase text-xl md:text-2xl tracking-tight mt-2">{title}</h3>
                <p className="mt-2 text-sm text-[color:rgba(245,242,232,0.7)] leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MECHANICS */}
      <section data-testid="mechanics">
        <div className="overline mb-3">Extras that keep it fun</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-5">
          The <span style={{ color: GREEN }}>community</span> layer
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {MECHANICS.map(({ Icon, title, body }) => (
            <div key={title} className="loot-card-flat p-5">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                style={{ backgroundColor: "rgba(118,184,42,0.14)", color: GREEN }}
              >
                <Icon size={18} />
              </div>
              <div className="font-display uppercase text-lg leading-tight">{title}</div>
              <div className="text-sm text-[color:rgba(245,242,232,0.7)] mt-2 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* STAY READY CTA */}
      <section className="loot-card p-8 md:p-10" data-testid="stay-ready">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${GREEN}22`, color: GREEN }}
          >
            <Bell size={22} />
          </div>
          <div className="flex-1">
            <div className="overline">Ready to play?</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mt-2">
              Stay ready. <span style={{ color: GREEN }}>Pay attention.</span>
            </h2>
            <p className="mt-2 text-[color:rgba(245,242,232,0.7)]">
              Turn on notifications so you're first to the clue. Drops can go live any time.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/drops" className="btn-loot inline-flex items-center gap-2" data-testid="cta-drops">
                See live drops <ArrowRight size={14} />
              </Link>
              <Link to="/waymarks" className="btn-ghost inline-flex items-center gap-2" data-testid="cta-waymarks">
                Explore waymarks <ArrowRight size={14} />
              </Link>
              <Link to="/loot-quest" className="btn-ghost inline-flex items-center gap-2" data-testid="cta-loot-quest">
                Loot Quest chests <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
